import Player from "@vimeo/player";
import WaveSurfer from "wavesurfer.js";
import YTPlayer from "yt-player";
import { runInAction } from "mobx";
import { heroState } from "./event-hero";

/** Wrapper to normalize 3rd party players  */
export default class JumboPlayer {
  constructor({ type, id, isBackground = null, playerStatus = null }) {
    this.type = type;
    this.id = id;
    this.isBackground = isBackground;
    this.wrapper = document.getElementById("combined-media-wrapper");
    this.playerStatus = playerStatus;
  }

  init = () => {
    const { type, id, isBackground, wrapper } = this;

    wrapper.innerHTML = "";
    wrapper.appendChild(DynamicMediaWrapper(type, id));

    const playerEl = wrapper.querySelector("#player");

    switch (type) {
      case "vimeo":
        this.player = new Player(playerEl, {
          id: id,
          background: isBackground,
        });
        this.player
          .setColor(WP.branding.focus)
          .catch((error) => console.warn("Couldn't use brand color:", error));
        this.player.on("playing", () => {
          this.playerStatus = "playing";
        });
        this.player.on("pause", () => {
          this.playerStatus = "pause";
        });
        this.player.on("ended", () => {
          this.playerStatus = "ended";
          if (typeof id === "string" && id.includes("event")) {
            runInAction(() => {
              heroState.resetLiveStream = true;
            });
          }
        });
        break;
      case "youtube":
        this.player = new YTPlayer(playerEl, {
          modestBranding: true,
        });
        this.player.load(id, { autoplay: true });
        break;
      case "ivs":
        registerIVSTech(videojs);
        registerIVSQualityPlugin(videojs);
        this.player = videojs(playerEl, {
          techOrder: ["AmazonIVS"],
          controls: true,
          autoplay: true,
          playsInline: true,
          controlBar: {
            playToggle: { replay: false },
            pictureInPictureToggle: false,
          },
        });
        const readyCallback = () => {
          this.player.enableIVSQualityPlugin();
          this.player.src(id);
          requestAnimationFrame(() => this.player.play());
        };
        this.player.ready(readyCallback);
        break;
      case "cloudflare":
        this.player = videojs(playerEl, {
          controls: true,
          autoplay: true,
          playsInline: true,
          controlBar: {
            playToggle: { replay: false },
            pictureInPictureToggle: false,
          },
        });
        this.player.src({
          src: id,
          type: "application/x-mpegURL",
        });
        this.player.on("error", (e) => {
          playerEl.classList.add("vjs-waiting");
        });
        this.player.on("ended", () => {
          runInAction(() => {
            heroState.resetLiveStream = true;
          });
        });
        break;
      case "wavesurfer":
        this.player = WaveSurfer.create({
          container: playerEl,
          scrollParent: false,
          height: 180,
          cursorColor: "transparent",
          waveColor: WP.audioPlayer.barColor,
          progressColor: WP.audioPlayer.waveProgressColor,
          responsive: true,
          backend: "MediaElementWebAudio",
        });
        this.player.load(id);
        // ? wavesurfer needs some event listeners as well
        (() => {
          const playPauseBtn = document.querySelector(".js-play-pause");
          playPauseBtn.addEventListener("click", () => this.player.playPause());
          this.player.on("pause", () => {
            playPauseBtn.classList.remove("state-play");
            playPauseBtn.classList.add("state-pause");
          });
          this.player.on("play", () => {
            playPauseBtn.classList.remove("state-pause");
            playPauseBtn.classList.add("state-play");
          });
        })();
        break;
    }

    if (isBackground) this.mute();
    else this.unmute();

    if (this.type !== "ivs") this.play(); //? IVS play is triggered in its "ready callback" above
  };

  play = () => {
    this.player?.play();
  };

  pause = () => {
    this.player?.pause();
  };

  destroy = () => {
    if (this.type === "ivs" || this.type === "cloudflare") {
      this.player?.dispose();
    } else {
      this.player?.destroy();
    }
  };

  mute = () => {
    if (this.type === "ivs" || this.type === "cloudflare") {
      this.player?.volume(0);
    } else {
      this.player?.setVolume(0);
    }
  };

  //? used in clip stat tracking (vimeo, youtube, and wavesurfer, so far...)
  getCurrentTime = () => this.player?.getCurrentTime();

  getPlayerStatus = () => {
    switch (this.type) {
      case "vimeo":
        return this.playerStatus;
      case "youtube":
        return this.player.getState();
      case "wavesurfer":
        return this.player.isPlaying() ? "playing" : null;
      default:
        //? not implemented for other types (yet?)
        return null;
    }
  };

  on = (e, fn) => this.player?.on(e, fn);

  off = (e, fn) => {
    switch (this.type) {
      case "vimeo":
        this.player.off(e, fn);
        break;

      case "wavesurfer":
        this.player.un(e, fn);
        break;
      default:
        null;
        break;
    }
  };

  unmute = () => {
    switch (this.type) {
      case "vimeo":
      case "wavesurfer":
        this.player?.setVolume(1);
        break;
      case "ivs":
      case "cloudflare":
        this.player?.volume(1);
        break;
      case "youtube":
        this.player?.setVolume(100);
        break;
      default:
        null;
        break;
    }
  };

  updateMedia = (id) => {
    //? only needs to support clip types, since those are the only cases where
    //? we would switch from one media to another of the same type back to back
    switch (this.type) {
      case "vimeo":
        this.player.loadVideo(id).then(() => this.play());
        break;
      case "wavesurfer":
        this.player.load(id);
        requestAnimationFrame(() => this.play());
        break;
      case "youtube":
        this.player.load(id, { autoplay: true });
        break;
      case "image":
      case "pdf":
        this.wrapper.innerHTML = "";
        this.wrapper.appendChild(DynamicMediaWrapper(this.type, id));
        break;
    }
  };
}

function DynamicMediaWrapper(type, id) {
  const wrapper = document.createElement("div");
  let htmlTag = "div",
    className,
    style,
    extras;

  switch (type) {
    case "wavesurfer":
      className = "hero-media-audio";
      extras = `
        <a class="js-play-pause play-button state-pause">
          <i class="fa fa-play"></i>
          <i class="fa fa-pause"></i>
        </a>
      `;
      break;
    case "pdf":
      className = "hero-media-pdf";
      extras = `
        <iframe
          id="pdf-viewer"
          src="${id}"
          width="580"
          height="326"
          frameborder="0"
          allow="autoplay; fullscreen"
          allowfullscreen=""
          style="height: 100%; width: 100%;"
        ></iframe>
      `;
      break;

    case "image":
      className = "hero-media-image";
      extras = `<img id="hero-image" src="${id}">`;
      break;

    case "ivs":
    case "cloudflare":
      className = "video-js vjs-big-play-centered";
      htmlTag = "video";
      style = { padding: "0 0 56.25%", width: "100%" };
      break;

    case "zoom":
      className = "hero-media-video";
      extras = `
        <iframe
          id="zoom-player"
          frameborder="0"
          scrolling="no"
          sandbox="allow-forms allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-top-navigation"
          allow="microphone; camera; fullscreen;"
          src="${id}"
        ></iframe>
      `;
      break;

    case "jitsi":
      className = "hero-media-video";
      extras = `
        <iframe
          id="jitsi-player"
          frameborder="0"
          scrolling="no"
          allow="microphone; camera; fullscreen; display-capture"
          src="${id}"
        >
        </iframe>
      `;
      break;

    case "rally":
      className = "hero-media-video";
      extras = `
        <iframe
          id="rally-player"
          frameborder="0"
          scrolling="yes"
          allow="microphone; camera; fullscreen; display-capture"
          src="${id}"
        >
        </iframe>
      `;
      break;

    case "royalslider":
      className = "hero-media-slider";
      extras = `
        <iframe
          id="royalslider-player"
          frameborder="0"
          scrolling="no"
          allow="microphone; camera; fullscreen; display-capture"
          src="${id}"
        >
        </iframe>
      `;
      break;

    default:
      className = "hero-media-video";
      break;
  }

  wrapper.className = className;
  if (style) Object.assign(wrapper.style, style);
  wrapper.innerHTML = `
    ${extras ? extras : ""}
    <${htmlTag} id="player"></${htmlTag}>
  `;

  return wrapper;
}
